import { useAuth } from "@ssf/auth-components";
import { guestUid } from "@ssf/play-common";
import {
  createStoredGuestId,
  getStoredGuestId,
  removeStoredGuestId,
} from "@ssf/play-components";
import type { ReactNode } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

type PlayerIdentity = {
  uid: string;
  name: string;
  kind: "member" | "guest";
};

type PlayerContextValue = {
  player?: PlayerIdentity;
  isPlayerConfirmed: boolean;
  continueAsPlayer(): void;
  continueAsGuest(): void;
};

const PlayerContext = createContext<PlayerContextValue | undefined>(undefined);

const getGuestName = (guestId: string) => {
  const number = 10_000 + (Number.parseInt(guestId.slice(0, 8), 16) % 90_000);
  return `Gäst ${number}`;
};

export const PlayerProvider = ({ children }: { children: ReactNode }) => {
  const { user, isInitiated } = useAuth();
  const [storedGuestId, setStoredGuestId] = useState(getStoredGuestId);
  const [confirmedPlayerUid, setConfirmedPlayerUid] = useState<string>();

  useEffect(() => {
    if (user && storedGuestId) {
      removeStoredGuestId();
      setStoredGuestId(undefined);
    }
  }, [storedGuestId, user]);

  const continueAsGuest = useCallback(() => {
    setStoredGuestId(getStoredGuestId() ?? createStoredGuestId());
  }, []);

  const player = useMemo<PlayerIdentity | undefined>(() => {
    if (!isInitiated) {
      return undefined;
    }
    if (user) {
      return {
        uid: `memdb_${user.profile.sub}`,
        name: user.profile.name || user.profile.sub,
        kind: "member",
      };
    }
    if (storedGuestId) {
      return {
        uid: guestUid(storedGuestId),
        name: getGuestName(storedGuestId),
        kind: "guest",
      };
    }
  }, [isInitiated, storedGuestId, user]);

  const continueAsPlayer = useCallback(() => {
    if (player) {
      setConfirmedPlayerUid(player.uid);
    }
  }, [player]);

  const isPlayerConfirmed =
    !!player && (player.kind === "guest" || player.uid === confirmedPlayerUid);

  return (
    <PlayerContext.Provider
      value={{ player, isPlayerConfirmed, continueAsPlayer, continueAsGuest }}
    >
      {children}
    </PlayerContext.Provider>
  );
};

export const usePlayer = () => {
  const context = useContext(PlayerContext);
  if (!context) {
    throw new Error("usePlayer must be used within PlayerProvider");
  }
  return context;
};
